import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  SignInBrandLogo,
  ErrorMessage,
  SignInButton,
  SignInBackButton,
  SignInField,
  SignInHeading,
  SignInHelp,
  SignInInput,
  SignInLayout,
  SignInNote,
  SignInRule,
  getHostAudience,
  getSignInUrls,
  getSupabase,
  publicApi,
  setCompanyAccessToken,
  useBranding,
} from '@l2u/web-shared';
import { supportEmailOf } from '@l2u/shared/brandingDefaults';
import { loginLinkBody, needsWorkspace } from '../lib/loginWorkspace.ts';
import {
  OTP_LENGTH,
  afterRefusal,
  canAutoSubmit,
  canVerifyNow,
  deleteBefore,
  errorForValue,
  fillAll,
  isCurrentAttempt,
  writeCell,
} from '../lib/otpCells.ts';
import { installSession } from '../lib/verifySession.ts';
import { companyPath, driverPath } from '../lib/audienceRoutes.ts';
import { isAccountLocked } from '../lib/lockedRedirect.ts';

/**
 * "Email me a sign-in code" — the portal's front door, which for a long time
 * had no handle on the inside.
 *
 * There is no password anywhere in this product: a session comes from an email.
 * That was fine for the first visit and hopeless for every one after it — a
 * lapsed session landed on "contact your administrator to receive a new
 * invitation", so a contractor returning months later to replace an expiring
 * certificate had to raise a support request to get back in.
 *
 * WHY A CODE RATHER THAN THE LINK THIS PAGE USED TO SEND. A magic link is a
 * bearer credential posted through a mail system nobody here controls:
 * corporate scanners prefetch it and spend its single use before the recipient
 * ever clicks, webmail rewrites it, and it opens in whichever browser the mail
 * client prefers rather than the tab the user started in. A typed code keeps
 * the credential in the session that asked for it.
 *
 * TWO STEPS. The email step is the old form with its button relabelled. The
 * code step is an OTP_LENGTH-cell input whose mechanics are in
 * ../lib/otpCells.ts, and
 * whose every rule there is the fix for a real failure: the code is ONE
 * contiguous string so there can be no holes; a paste and a mobile autofill
 * take the same path; and the auto-submit passes its value EXPLICITLY, because
 * reading `code` from the closure in the same tick as `setCode` verifies the
 * PREVIOUS code.
 *
 * The send confirmation is deliberately the SAME whatever happened — sent,
 * unknown address, deactivated account — and so is every verify refusal
 * ("Invalid or expired code", rendered verbatim from the API). Both endpoints
 * are unauthenticated and take an email, so a message that distinguished those
 * cases would turn this page into an account-enumeration oracle over a
 * customer's contractor list. THE DESIGN CANVAS PROPOSED "Sent to
 * {email}. Eight digits, valid for 10 minutes." — that sentence asserts the
 * address has an account, and is exactly the oracle this copy avoids. It was
 * not taken.
 *
 * WHICH workspace is a separate question from which address, and not an
 * enumeration risk the way an address is: the caller supplies the workspace and
 * it reveals nothing about who has an account. The API resolves it from the
 * request hostname or the browser's Origin, which costs the user nothing on a
 * tenant subdomain. On the shared platform host neither fires, so the slug
 * comes from /c/login/:tenantSlug when a link carries it, and otherwise the
 * Workspace field is revealed only once the API says it could not tell. It then
 * has to persist into the code step, or `verify-code` would resolve a different
 * workspace than `login-code` did. See lib/loginWorkspace.ts.
 *
 * PRESENTATION ONLY was changed when the "Compliance Sign-in" canvas landed:
 * every branch, guard, ref and message below is the same one that was here
 * before. The canvas also dropped the Workspace field, the resend cooldown and
 * the "use the newest email" sentence; all three are kept, because each is the
 * fix for a real failure documented above and beside it.
 */

/** Seconds before "Resend code" becomes available again. */
const RESEND_SECONDS = 30;

type Step = 'email' | 'code';

/**
 * A link to the OTHER audience's sign-in page. Where both families share this
 * origin (the legacy shared base, local dev) that is a router link to the
 * prefixed path. On a per-audience host it is a different HOSTNAME — the driver
 * sign-in is not a route on the company host at all — so it is a plain anchor
 * to the URL /api/config supplied. Null when an older API supplied none: the
 * caller then drops the whole line rather than point it at a path this host
 * 404s.
 */
function otherSignIn(family: 'company' | 'driver', label: string): ReactNode {
  if (getHostAudience() === null) {
    return (
      <Link to={family === 'company' ? companyPath('/login') : driverPath('/login')}>{label}</Link>
    );
  }
  const href = getSignInUrls()?.[family];
  return href ? <a href={href}>{label}</a> : null;
}

export function LoginCodeRequest({ kind }: { kind: 'company' | 'driver' }) {
  const { branding } = useBranding();
  const supportEmail = supportEmailOf(branding);
  const { tenantSlug } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [workspace, setWorkspace] = useState('');
  const [askWorkspace, setAskWorkspace] = useState(false);
  const [code, setCode] = useState('');
  // The last value the server refused. Kept so the same wrong code cannot
  // auto-resubmit itself — see otpCells.ts's notes on the verify storm.
  const [lastRefused, setLastRefused] = useState<string | null>(null);
  // The error that came with `lastRefused`, so it can be put BACK when the
  // user reassembles that same value — see errorForValue in otpCells.ts. A ref
  // rather than state: it is never rendered on its own, only fed to setError.
  const refusalError = useRef<unknown>(null);
  // Which attempt the form is on. Bumped whenever the user moves on from the
  // code currently being verified — "Change email", and any send/resend — so a
  // response that arrives afterwards can be recognised as stale and discarded.
  // A ref, not state: it is read inside an async closure that must see the
  // CURRENT value, which a state snapshot would not give it. See
  // isCurrentAttempt in otpCells.ts.
  const attempt = useRef(0);
  // Separate flags on purpose: a resend must not put the "Sign in" button into
  // its verifying state, which would claim something untrue about the code the
  // user has not typed yet.
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [countdown, setCountdown] = useState(RESEND_SECONDS);
  const [error, setError] = useState<unknown>(null);

  const cellRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Focus is REQUESTED here and applied after render: the target cell may not
  // exist yet at the moment the code grows, so focusing inline silently no-ops.
  const wantFocus = useRef<number | null>(null);

  useEffect(() => {
    if (wantFocus.current == null) return;
    const i = wantFocus.current;
    wantFocus.current = null;
    cellRefs.current[i]?.focus();
  });

  // Resend cooldown — only ticks on the code step.
  useEffect(() => {
    if (step !== 'code' || countdown <= 0) return;
    const id = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [step, countdown]);

  const canSend = !!email.trim() && !(askWorkspace && !workspace.trim());
  // The SAME call `verifyWith` guards on, so the button cannot look pressable
  // while a submit would be refused. `sending` counts: a resend in flight is
  // about to invalidate whatever is in the cells.
  const canVerify = canVerifyNow(code, { verifying, sending });

  /** Ask the API to email a code. `next` distinguishes the first send from a resend. */
  async function send(next: 'code' | 'resend') {
    if (!canSend || sending || verifying) return;
    // Any send moves the form on from whatever code was last submitted, so a
    // verify still on the wire must not be allowed to land. (The guard above
    // already refuses to START a send mid-verify; this covers the reverse
    // order, and costs nothing.)
    attempt.current += 1;
    setSending(true);
    setError(null);
    try {
      await publicApi.post(
        `/api/auth/${kind}/login-code`,
        loginLinkBody(email, tenantSlug, workspace),
      );
      // A resend invalidates the code already in the cells, so clear them —
      // leaving the old digits visible would invite the user to submit a code
      // GoTrue has just thrown away. A fresh code also makes any earlier
      // refusal irrelevant.
      setCode('');
      setLastRefused(null);
      refusalError.current = null;
      setCountdown(RESEND_SECONDS);
      if (next === 'code') setStep('code');
      wantFocus.current = 0;
    } catch (err) {
      // The API could not tell which workspace: reveal the field and let them
      // say, rather than showing an error they cannot act on. Every OTHER
      // failure must still surface — a network blip quietly becoming "type your
      // workspace" would blame the user for an outage.
      if (needsWorkspace(err) && !askWorkspace) {
        setAskWorkspace(true);
        // The Workspace field only exists on the email step, so revealing it
        // from a RESEND would otherwise set a flag nobody can see and leave the
        // user staring at an unchanged code step. A no-op on the first send,
        // where we are already here.
        setStep('email');
      } else {
        // Only transport/workspace-level problems reach here; the endpoint
        // answers 200 for every outcome that concerns a particular address.
        setError(err);
      }
    } finally {
      setSending(false);
    }
  }

  /**
   * Verify an EXPLICIT value. Never reads `code` from the closure: an
   * auto-submit fired in the same tick as `setCode` would otherwise verify the
   * previous code, or bail out because the stale value still looked too short.
   */
  async function verifyWith(value: string) {
    // The one gate for every path in: auto-submit, Enter and the button. It
    // refuses while a RESEND is in flight, because that request is minting a
    // code which invalidates the digits being submitted here. See canVerifyNow.
    if (!canVerifyNow(value, { verifying, sending })) return;
    // Snapshot BEFORE the await. "Change email" and any send bump this, so if
    // either happened while the request was on the wire, the answer below is
    // about a form the user has already left. See isCurrentAttempt.
    const startedAt = attempt.current;
    setVerifying(true);
    setError(null);
    try {
      const tokens = await publicApi.post<{ access_token: string; refresh_token: string }>(
        `/api/auth/${kind}/verify-code`,
        { ...loginLinkBody(email, tenantSlug, workspace), code: value },
      );
      const supabase = getSupabase();
      // The ordering lives in lib/verifySession.ts so it can be tested: there
      // are TWO windows in which the user can click "Change email" — before
      // setSession and, because setSession is itself async, after it resolves
      // — and only the second one has a session in browser storage to unwind.
      // The last two steps are the ones /c/callback performs after a magic
      // link, in the same order: api.ts reads the token from this holder, and
      // CompanyAuthProvider hears the session change through onAuthStateChange.
      await installSession(tokens, startedAt, {
        setSession: (t) => supabase.auth.setSession(t),
        // Local scope, never the default global one, which would revoke the
        // refresh token server-side and sign this account out of every other
        // device. Undoing one browser's storage is all that is wanted.
        signOutLocal: async () => {
          await supabase.auth.signOut({ scope: 'local' });
        },
        publishToken: setCompanyAccessToken,
        navigate: () =>
          navigate(kind === 'company' ? companyPath('') : driverPath(''), { replace: true }),
        currentAttempt: () => attempt.current,
      });
    } catch (err) {
      // The SAME staleness check on the refusal path. A stale refusal would
      // otherwise plant an error message and a `lastRefused` against a code the
      // form is no longer holding — on the email step, where there are no cells
      // to explain it. `verifying` is already false here (whoever bumped the
      // counter cleared it), so there is nothing to unwind.
      if (!isCurrentAttempt(startedAt, attempt.current)) return;
      // The one refusal that is NOT rendered inline: a CORRECT code for a
      // locked account. The api tells it apart from the uniform 401 only after
      // the code is proven, so this reveals nothing the user did not just
      // prove. No session was issued, so there is nothing to unwind.
      if (isAccountLocked(err)) {
        navigate(kind === 'company' ? companyPath('/locked') : driverPath('/locked'), {
          replace: true,
        });
        return;
      }
      // Rendered verbatim: the API deliberately answers one flat message for
      // every refusal, and inventing a friendlier local variant would start
      // guessing at which of them happened.
      setError(err);
      // Remembered alongside the value it refused, so retyping or re-pasting
      // that exact code brings the message back instead of leaving the user in
      // front of a full form with nothing to read. See errorForValue.
      refusalError.current = err;
      // Clear the cells and go back to the first one. Leaving the digits in
      // place would keep the input "complete", so the next keystroke of the
      // user's correction would fire another request — and the one after that,
      // and the one after that. See otpCells.ts.
      const reset = afterRefusal(value);
      setCode(reset.code);
      setLastRefused(reset.lastRefused);
      wantFocus.current = reset.focus;
      setVerifying(false);
      return;
    }
    // Not in a `finally`: on success this component is being navigated away
    // from, and clearing the flag would flash the button back to "Sign in".
  }

  /**
   * A whole code arriving at once — a paste, or a multi-character insert from
   * mobile one-time-code autofill (which ignores maxLength and drops the full
   * code into a single cell). One path for both.
   */
  function fillFrom(raw: string) {
    const r = fillAll(raw);
    if (!r) return;
    setCode(r.code);
    // NOT an unconditional clear: re-pasting the refused code puts its message
    // back, because `canAutoSubmit` below will decline to send it.
    setError(errorForValue(r.code, lastRefused, refusalError.current));
    wantFocus.current = r.focus;
    // `canAutoSubmit` and not `r.submit` alone: re-pasting the code the server
    // just refused refills every cell at once, and would otherwise spend
    // another request on a value that cannot have become valid.
    if (r.submit && canAutoSubmit(r.code, lastRefused)) void verifyWith(r.code);
  }

  function onCellInput(i: number, raw: string) {
    const digits = raw.replace(/\D/g, '');
    if (digits.length > 1) {
      fillFrom(digits);
      return;
    }
    const r = writeCell(code, i, digits);
    setCode(r.code);
    // After the write, not before it: the message clears while the value is
    // changing and returns if the user retypes the refused code exactly.
    setError(errorForValue(r.code, lastRefused, refusalError.current));
    if (r.focus != null) wantFocus.current = r.focus;
    if (r.submit && canAutoSubmit(r.code, lastRefused)) void verifyWith(r.code);
  }

  function onCellKey(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void verifyWith(code);
      return;
    }
    if (e.key === 'Backspace' && !code[i]) {
      e.preventDefault();
      const r = deleteBefore(code, i);
      setCode(r.code);
      wantFocus.current = r.focus;
    }
    if (e.key === 'ArrowLeft' && i > 0) cellRefs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i + 1 < OTP_LENGTH) cellRefs.current[i + 1]?.focus();
  }

  const audience = kind === 'driver' ? 'driver' : 'company';
  // Cell geometry, from the canvas. The DRIVER cells are not enlarged the way
  // the driver's other controls are: eight of them have to fit on one row at
  // 375px (see the row's own note below), and the canvas sizes them the same
  // for both audiences for exactly that reason.
  const cellBorder = kind === 'driver' ? '#DED3B9' : '#DDDACD';

  const otherLink =
    kind === 'driver'
      ? otherSignIn('company', 'Use the company sign-in')
      : otherSignIn('driver', 'Use the driver sign-in');
  /** The link to the other audience's sign-in, and the support address. */
  const help = (
    <SignInHelp>
      {otherLink &&
        (kind === 'driver' ? (
          <div>Signing in for a business rather than as a driver? {otherLink}.</div>
        ) : (
          <div>Are you a driver? {otherLink}.</div>
        ))}
      <div>
        Still stuck? Email <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
      </div>
    </SignInHelp>
  );

  return (
    // `driver-portal` opts this page into the phone touch-target and
    // 16px-input rules in index.css — see DriverShell. Gated on `kind`: the
    // same form is /c/login for a company user on a desktop.
    //
    // Carried over from LoginLinkRequest.tsx, which this component replaced:
    // PR #82 added the hook there and that file was deleted, so without this
    // the merge would have silently reverted #82's work on the driver sign-in
    // page.
    //
    // WHAT IT STILL REACHES, now the controls are styled inline: nothing on
    // this page. index.css's rules are a stylesheet, and an inline style beats
    // one — the driver's fields keep the canvas's 58px/17px and the cells keep
    // 54px/19px rather than being pulled back to 44px/16px. Both are ABOVE
    // those floors, so the class is inert here and load-bearing everywhere else
    // in the driver portal. It stays because removing it would make the next
    // control added to this page silently exempt.
    <SignInLayout audience={audience} className={kind === 'driver' ? 'driver-portal' : ''}>
      {/* The tenant's logo, or its name when none is uploaded; the product
            mark only for an unbranded workspace. See SignInBrandLogo. */}
      <SignInBrandLogo />

      {step === 'code' ? (
        <>
          {/* Deliberately NOT disabled while verifying. A verify can hang,
                and trapping the user on a screen they are trying to leave is
                worse than the race — which is closed properly instead, by the
                attempt counter: the in-flight response is discarded rather
                than applied to this now-abandoned address. `verifying` is
                cleared here because that request will never resolve into
                anything. */}
          <SignInBackButton
            onClick={() => {
              attempt.current += 1;
              setStep('email');
              setCode('');
              setLastRefused(null);
              refusalError.current = null;
              setError(null);
              setVerifying(false);
            }}
          >
            ← Change email
          </SignInBackButton>

          {/* The length comes from OTP_LENGTH, never a word. This branch
                moved the constant 6 → 8 by hand and hard-coded copy did not
                follow; phrased to sit after a dash so no indefinite article
                has to agree with the numeral ("a 6-" but "an 8-").
                "If an account exists for" is load-bearing — see the header. */}
          <SignInHeading title="Enter your sign-in code">
            If an account exists for{' '}
            <strong className="font-semibold text-[#14120D]">{email.trim()}</strong>, a sign-in code
            is on its way — {OTP_LENGTH} digits. It works once and expires in about an hour.
          </SignInHeading>

          {/* Not optional copy. Asking for another code invalidates the
                previous one server-side, so a user who taps resend and then
                types the first email's code is refused with nothing to explain
                why. This sentence is the only mitigation there is. */}
          <SignInNote>
            If you asked more than once, use the code from the <strong>newest</strong> email.
          </SignInNote>

          {/* OTP_LENGTH cells on ONE row, never wrapped and never scrolled:
                a code broken across two lines reads as two codes, and a row
                that scrolls hides digits the user has already typed. Eight
                cells is the tight case — at 375px the column leaves ~331px
                inside its 22px padding, so seven 7px gaps leave each cell at
                ~35px. `min-w-0` is what lets them shrink at all: a flex item
                refuses to go below its content width without it. Adding a cell
                without re-measuring this is how the row starts overflowing. */}
          <div className="flex gap-[7px]">
            {Array.from({ length: OTP_LENGTH }, (_, i) => (
              <input
                key={i}
                type="text"
                inputMode="numeric"
                maxLength={1}
                autoComplete="one-time-code"
                aria-label={`Digit ${i + 1}`}
                disabled={verifying}
                value={code[i] ?? ''}
                ref={(el) => {
                  cellRefs.current[i] = el;
                }}
                onChange={(e) => onCellInput(i, e.target.value || '')}
                onKeyDown={(e) => onCellKey(i, e)}
                onPaste={(e) => {
                  e.preventDefault();
                  fillFrom(e.clipboardData.getData('text') || '');
                }}
                // maxLength=1 makes the browser silently drop a keystroke
                // into an already-filled cell, so onChange never fires.
                // Selecting on focus means typing overwrites instead of
                // doing nothing at all.
                onFocus={(e) => e.currentTarget.select()}
                className="w-full min-w-0 text-center tabular-nums focus:outline-none disabled:opacity-60"
                style={{
                  boxSizing: 'border-box',
                  height: 54,
                  padding: 0,
                  fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 19,
                  color: '#14120D',
                  // A filled cell darkens its border and goes pure white, so
                  // progress along the row is visible without counting.
                  background: code[i] ? '#FFFFFF' : '#FFFDF7',
                  border: `1.5px solid ${code[i] ? '#244232' : cellBorder}`,
                  borderRadius: 9,
                  transition: 'border-color .18s ease, box-shadow .18s ease',
                }}
              />
            ))}
          </div>

          <div
            className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1"
            style={{ fontSize: 13.5, color: '#5B5950' }}
          >
            <span>Didn’t get it? Check your spam folder.</span>
            {countdown > 0 ? (
              <span className="whitespace-nowrap tabular-nums">Resend in {countdown}s</span>
            ) : (
              <button
                type="button"
                className="cursor-pointer whitespace-nowrap border-none bg-transparent p-0 font-semibold text-[#244232] underline decoration-1 underline-offset-[3px] hover:text-[#14120D] disabled:opacity-50"
                disabled={sending || verifying}
                onClick={() => void send('resend')}
              >
                {sending ? 'Sending…' : 'Resend code'}
              </button>
            )}
          </div>

          {/* The live region is mounted unconditionally so a refusal is
                announced as a change INSIDE it, rather than relying on every
                screen reader noticing a freshly inserted role=alert node. */}
          <div role="alert" className="empty:hidden">
            <ErrorMessage error={error} />
          </div>

          <SignInButton
            audience={audience}
            onClick={() => void verifyWith(code)}
            loading={verifying}
            disabled={!canVerify}
          >
            Verify and continue
          </SignInButton>

          <SignInRule />
          {help}
        </>
      ) : (
        <form
          className="flex flex-col gap-6"
          onSubmit={(e) => {
            e.preventDefault();
            void send('code');
          }}
        >
          <SignInHeading
            eyebrow={kind === 'driver' ? 'Driver sign-in' : 'Company sign-in'}
            title={
              kind === 'driver'
                ? 'Sign in to your driver profile'
                : 'Sign in to your supplier portal'
            }
          >
            Enter the email address{' '}
            {kind === 'driver' ? 'your invitation was sent to' : 'we hold for your company'} and
            we’ll send you a sign-in code.
          </SignInHeading>

          <div className="flex flex-col gap-4">
            <SignInField label="Email address" audience={audience}>
              <SignInInput
                audience={audience}
                type="email"
                autoComplete="username"
                inputMode="email"
                // Yields to the Workspace field once that is revealed, so only
                // one input ever claims focus.
                autoFocus={!askWorkspace}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={sending}
                placeholder={kind === 'driver' ? 'you@example.com' : 'you@company.com.au'}
              />
            </SignInField>

            {askWorkspace && (
              <div className="flex flex-col gap-1.5">
                <SignInField label="Workspace" audience={audience}>
                  <SignInInput
                    audience={audience}
                    autoFocus
                    required
                    value={workspace}
                    onChange={(e) => setWorkspace(e.target.value)}
                    disabled={sending}
                    placeholder="acme"
                  />
                </SignInField>
                <p className="m-0" style={{ fontSize: 12.5, lineHeight: 1.5, color: '#5B5950' }}>
                  We couldn’t tell which workspace you belong to from this address. It’s the name in
                  the link you were sent — pasting the whole link works too.
                </p>
              </div>
            )}
          </div>

          <div role="alert" className="empty:hidden">
            <ErrorMessage error={error} />
          </div>

          <SignInButton audience={audience} type="submit" loading={sending} disabled={!canSend}>
            Email me a sign-in code
          </SignInButton>

          {/* The canvas's passwordless callout, with the numbers this product
                actually has: OTP_LENGTH rather than a hard-coded "8-digit", and
                the expiry the API and the code step both state. The canvas said
                "10 minutes", which nothing here supports.

                The length sits AFTER A DASH, never after an article, for the
                same reason the code step's copy does — see the note there. A
                first draft of this sentence read "We email a {OTP_LENGTH}-digit
                code", which renders "a 8-digit code" at today's length and would
                silently become correct again only if the constant went back to
                a consonant-sounding numeral. The browser walk caught it; no
                test can, because none of them read English. */}
          <SignInNote>
            No password to remember. We email a sign-in code — {OTP_LENGTH} digits — that works once
            and expires in about an hour.
          </SignInNote>

          <SignInRule />
          {help}
        </form>
      )}
    </SignInLayout>
  );
}

export function CompanyLogin() {
  return <LoginCodeRequest kind="company" />;
}

export function DriverLogin() {
  return <LoginCodeRequest kind="driver" />;
}
